import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import DemoLayout from "@/components/demo/DemoLayout";
import { ArrowLeft, MapPin, Briefcase, Calendar, Star, Users } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/demo`;
const REGION_FLAGS = {
  canada: "\u{1F1E8}\u{1F1E6}", usa: "\u{1F1FA}\u{1F1F8}", jamaica: "\u{1F1EF}\u{1F1F2}",
  barbados: "\u{1F1E7}\u{1F1E7}", trinidad_tobago: "\u{1F1F9}\u{1F1F9}", panama: "\u{1F1F5}\u{1F1E6}"
};

function LikertBar({ label, score, max = 5 }) {
  const pct = (score / max) * 100;
  const color = score >= 4 ? "var(--color-success)" : score >= 3 ? "var(--color-warning)" : "var(--color-danger)";
  return (
    <div className="flex items-center gap-3">
      <div className="w-40 text-[12px] text-right" style={{ color: "var(--text-secondary)" }}>{label}</div>
      <div className="flex-1 progress-m"><div className="progress-fill" style={{ width: `${pct}%`, background: color }} /></div>
      <div className="metric-val text-[13px] w-6 text-right" style={{ color }}>{score}</div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div className="card-m p-5">
      <h3 className="text-sm font-semibold mb-3" style={{ color: "var(--text-primary)" }}>{title}</h3>
      {children}
    </div>
  );
}

function Narrative({ label, text }) {
  if (!text || text === '""' || text.length < 3) return null;
  const clean = text.replace(/^"|"$/g, "").replace(/\\n/g, "\n");
  return (
    <div className="mb-3">
      <div className="text-[10px] font-semibold tracking-wider mb-1" style={{ color: "var(--text-muted)" }}>{label}</div>
      <div className="text-[13px] leading-relaxed whitespace-pre-line" style={{ color: "var(--text-secondary)" }}>{clean}</div>
    </div>
  );
}

export default function DemoEmployeeProfile() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/people/${id}`).then(r => setData(r.data)).catch(console.error).finally(() => setLoading(false));
  }, [id]);

  if (loading || !data) {
    return <DemoLayout><div className="flex items-center justify-center h-64"><div className="animate-pulse text-sm" style={{ color: "var(--text-muted)" }}>Loading profile...</div></div></DemoLayout>;
  }

  const p = data.profile;
  const initials = p.full_name ? p.full_name.split(" ").map(w => w[0]).join("").slice(0, 2).toUpperCase() : "??";
  const gapLabel = data.mv_data?.gap_category === "under_rater" ? "Under-rater (Hidden Talent)" : data.mv_data?.gap_category === "over_rater" ? "Over-rater (Coaching Need)" : "Aligned";
  const gapColor = data.mv_data?.gap_category === "under_rater" ? "var(--color-info)" : data.mv_data?.gap_category === "over_rater" ? "var(--color-danger)" : "var(--text-muted)";

  return (
    <DemoLayout>
      <div className="space-y-4" data-testid="employee-profile">
        <button onClick={() => navigate(-1)} className="flex items-center gap-1 text-[13px] mb-2 transition-colors" style={{ color: "var(--color-accent)" }}>
          <ArrowLeft className="w-4 h-4" /> Back to Directory
        </button>

        {/* Header */}
        <div className="card-m p-5 flex flex-wrap items-start gap-5">
          <div className="w-16 h-16 rounded-full flex items-center justify-center text-xl font-bold" style={{ background: "var(--color-accent-bg)", color: "var(--color-accent)" }}>
            {initials}
          </div>
          <div className="flex-1 min-w-[200px]">
            <h1 className="text-xl font-semibold" style={{ color: "var(--text-primary)" }}>{p.full_name}</h1>
            <div className="text-[13px] mt-0.5" style={{ color: "var(--text-secondary)" }}>{p.job_title || "—"}</div>
            <div className="text-[12px]" style={{ color: "var(--text-muted)" }}>{p.department || "—"}</div>
            <div className="flex flex-wrap gap-4 mt-2 text-[11px]" style={{ color: "var(--text-muted)" }}>
              {p.region && <span className="flex items-center gap-1">{REGION_FLAGS[p.region]} {p.location || p.region}</span>}
              {p.hire_date && <span className="flex items-center gap-1"><Calendar className="w-3 h-3" /> Hired {p.hire_date}</span>}
              {p.years_at_sagicor && <span className="flex items-center gap-1"><Briefcase className="w-3 h-3" /> {p.years_at_sagicor} years</span>}
              {p.manager_name && <span className="flex items-center gap-1"><Users className="w-3 h-3" /> {p.manager_name}</span>}
            </div>
          </div>
          <div className="flex gap-3">
            <div className="card-m p-3 text-center min-w-[80px]">
              <div className="metric-val text-xl">{data.sa_avg || "—"}</div>
              <div className="text-[10px]" style={{ color: "var(--text-muted)" }}>SA Score</div>
            </div>
            {data.mv_data && (
              <div className="card-m p-3 text-center min-w-[80px]">
                <div className="metric-val text-xl">{data.mv_data.mv_avg || "—"}</div>
                <div className="text-[10px]" style={{ color: "var(--text-muted)" }}>MV Score</div>
              </div>
            )}
            <div className="card-m p-3 text-center min-w-[80px]">
              <div className="text-[10px]" style={{ color: "var(--text-muted)" }}>Status</div>
              <span className={`badge-m mt-1 ${p.status === "completed" ? "badge-success" : p.status === "started" ? "badge-warning" : "badge-muted"}`}>
                {p.status === "completed" ? "Complete" : p.status === "started" ? "In Progress" : "Not Started"}
              </span>
            </div>
          </div>
        </div>

        {/* Profile Details */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <div className="lg:col-span-2 space-y-4">
            {/* SA vs MV Gap */}
            {data.mv_data && (
              <Section title="SA vs MV Gap Analysis">
                <div className="flex items-center gap-4 mb-3">
                  <div className="text-[13px]" style={{ color: "var(--text-secondary)" }}>
                    Self-Assessment: <span className="metric-val">{data.sa_avg}</span>
                  </div>
                  <div className="text-[13px]" style={{ color: "var(--text-secondary)" }}>
                    Manager Validation: <span className="metric-val">{data.mv_data.mv_avg}</span>
                  </div>
                  <div className="text-[13px]" style={{ color: "var(--text-secondary)" }}>
                    Gap: <span className="metric-val" style={{ color: gapColor }}>{data.mv_data.gap > 0 ? "+" : ""}{data.mv_data.gap}</span>
                  </div>
                </div>
                <div className="badge-m" style={{ background: `${gapColor}15`, color: gapColor }}>{gapLabel}</div>
              </Section>
            )}

            {/* Manager Relationship */}
            {Object.keys(data.manager_relationship || {}).length > 0 && (
              <Section title="Manager Relationship">
                <div className="space-y-2">
                  {Object.entries(data.manager_relationship).map(([k, v]) => (
                    <LikertBar key={k} label={k} score={v} />
                  ))}
                </div>
              </Section>
            )}

            {/* Org Satisfaction */}
            {Object.keys(data.org_satisfaction || {}).length > 0 && (
              <Section title="Organizational Satisfaction">
                <div className="space-y-2">
                  {Object.entries(data.org_satisfaction).map(([k, v]) => (
                    <LikertBar key={k} label={k} score={v} />
                  ))}
                </div>
              </Section>
            )}

            {/* Career Aspirations */}
            {(data.career?.short_term || data.career?.long_term) && (
              <Section title="Career Aspirations">
                <Narrative label="1-2 YEAR GOAL" text={data.career.short_term} />
                <Narrative label="3-5 YEAR GOAL" text={data.career.long_term} />
                <Narrative label="IDEAL NEXT ROLE" text={data.career.ideal_role} />
              </Section>
            )}

            {/* Key Narratives */}
            {Object.values(data.narratives || {}).some(v => v && v.length > 5) && (
              <Section title="Key Narratives">
                <Narrative label="CAREER JOURNEY" text={data.narratives.career_journey} />
                <Narrative label="BIGGEST CONTRIBUTION" text={data.narratives.biggest_contribution} />
                <Narrative label="KEY INITIATIVES" text={data.narratives.key_initiatives} />
                <Narrative label="CHALLENGES" text={data.narratives.challenges} />
                <Narrative label="LEADERSHIP STYLE" text={data.narratives.leadership_style} />
              </Section>
            )}

            {/* START/STOP/CONTINUE */}
            {(data.narratives?.start_doing || data.narratives?.stop_doing || data.narratives?.continue_doing) && (
              <Section title="Organizational Perspective (Start/Stop/Continue)">
                <Narrative label="START DOING" text={data.narratives.start_doing} />
                <Narrative label="STOP DOING" text={data.narratives.stop_doing} />
                <Narrative label="CONTINUE DOING" text={data.narratives.continue_doing} />
              </Section>
            )}
          </div>

          {/* Right sidebar */}
          <div className="space-y-4">
            {/* Quick Info */}
            <Section title="Profile">
              <div className="space-y-2 text-[12px]">
                {[
                  ["Email", p.email],
                  ["Grade", p.job_grade],
                  ["Department", p.department],
                  ["Business Unit", p.business_unit],
                  ["Manager", p.manager_name],
                  ["Performance (2024)", p.performance_rating],
                  ["Assessment ID", p.assessment_id],
                  ["Type", p.assessment_type === "manager_validation" ? "Manager Validation" : "Self Assessment"],
                  ["Responses", data.response_count],
                ].map(([label, val]) => val && (
                  <div key={label} className="flex justify-between">
                    <span style={{ color: "var(--text-muted)" }}>{label}</span>
                    <span className="font-medium text-right" style={{ color: "var(--text-primary)" }}>{val}</span>
                  </div>
                ))}
              </div>
            </Section>

            {/* Role Skills */}
            {Object.keys(data.role_skills || {}).length > 0 && (
              <Section title="Role-Specific Skills">
                <div className="space-y-1.5">
                  {Object.entries(data.role_skills).map(([k, v]) => (
                    <div key={k} className="flex items-center gap-2">
                      <div className="flex-1 progress-m">
                        <div className="progress-fill" style={{
                          width: `${(v / 5) * 100}%`,
                          background: v >= 4 ? "var(--color-success)" : v >= 3 ? "var(--color-warning)" : "var(--color-danger)",
                        }} />
                      </div>
                      <span className="metric-val text-[11px] w-4 text-right">{v}</span>
                    </div>
                  ))}
                </div>
                <div className="text-[10px] mt-2" style={{ color: "var(--text-muted)" }}>
                  Avg: {data.sa_avg || "—"} / 5
                </div>
              </Section>
            )}

            {/* Domain Knowledge */}
            {Object.keys(data.domain_knowledge || {}).length > 0 && (
              <Section title="Domain Knowledge">
                <div className="space-y-1.5">
                  {Object.entries(data.domain_knowledge).map(([k, v]) => (
                    <div key={k} className="flex items-center gap-2">
                      <div className="flex-1 progress-m">
                        <div className="progress-fill" style={{
                          width: `${(v / 5) * 100}%`,
                          background: v >= 4 ? "var(--color-success)" : v >= 3 ? "var(--color-warning)" : "var(--color-danger)",
                        }} />
                      </div>
                      <span className="metric-val text-[11px] w-4 text-right">{v}</span>
                    </div>
                  ))}
                </div>
              </Section>
            )}

            {/* Technologies */}
            {p.technologies?.length > 0 && (
              <Section title="Technologies">
                <div className="flex flex-wrap gap-1.5">
                  {p.technologies.map((t, i) => (
                    <span key={i} className="badge-m badge-info text-[10px]">{t.name || t}</span>
                  ))}
                </div>
              </Section>
            )}
          </div>
        </div>
      </div>
    </DemoLayout>
  );
}
